import { Capacitor } from '@capacitor/core';
import OneSignalPlugin from 'onesignal-cordova-plugin';
import OneSignalV3 from 'onesignal-cordova-plugin-v3';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import captureSpecificError from '../../../services/sentry';
import { useOneSignalSetup } from './use-one-signal';
import { transitionToNextRoute } from '../../../services/route/route-navigation';

const OneSignal = OneSignalPlugin;

window.OneSignal5 = OneSignal;
window.OneSignal3 = OneSignalV3;

const OneSignalComp = (props) => {
  const { currentMember } = props;

  const history = useHistory();

  useOneSignalSetup(currentMember);

  useEffect(() => {
    if (window?.DECKEE_CONFIG?.oneSignalAppID && Capacitor.isNativePlatform()) {
      try {
        const oneSignalAppID = window?.DECKEE_CONFIG?.oneSignalAppID;
        // Have to handle two versions of OneSignal at once
        // so that we can release the app independently

        const iosSettings = {};
        iosSettings.kOSSettingsKeyAutoPrompt = false;
        iosSettings.kOSSettingsKeyInAppLaunchURL = false;

        if (OneSignalV3.setAppId && OneSignalV3.setNotificationOpenedHandler) {
          // Finally try onesignal 3.0.0
          OneSignalV3.setAppId(oneSignalAppID);

          OneSignalV3.setNotificationOpenedHandler((jsonData) => {
            if (jsonData?.notification?.additionalData?.route) {
              const route = jsonData?.notification?.additionalData?.route;

              if (route.split('/').length > 1) {
                history.push(jsonData?.notification?.additionalData?.route);
              }

              if (jsonData?.notification?.additionalData?.routeParams) {
                transitionToNextRoute(
                  jsonData.notification.additionalData.route,
                  jsonData.notification.additionalData.routeParams,
                );
              } else {
                transitionToNextRoute(
                  jsonData.notification.additionalData.route,
                );
              }
            }
          });
        }
      } catch (error) {
        try {
          captureSpecificError(
            error,
            'containers/app-main-client/src/components/null-components/OneSignal/OneSignal.jsx',
          );
        } catch {
          // If it fails to log to sentry, oh well...
        }
        // eslint-disable-next-line no-console
        console.log(
          `An error occurred while initializing OneSignal: ${error?.message}`,
        );
      }
    }
    // Disabling to keep functionality
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentMember?.isOnBoardingComplete && Capacitor.isNativePlatform()) {
      if (OneSignalV3.getPermissionSubscriptionState) {
        OneSignalV3.getPermissionSubscriptionState((status) => {
          if (!status?.permissionStatus?.hasPrompted) {
            OneSignalV3.addTrigger('ios_prompt', 'true');
          } else {
            OneSignalV3.promptForPushNotificationsWithUserResponse(
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              () => {},
            );
          }
        });
      } else if (OneSignalV3.getDeviceState) {
        OneSignalV3.getDeviceState((state) => {
          /*
            NotificationPermissionStatus
            1 - "Denied" - The app isn't authorized to schedule or receive notifications.
          */
          if (!state?.subscribed && state?.notificationPermissionStatus !== 1) {
            OneSignalV3.addTrigger('ios_prompt', 'true');
          }
        });
      }
      // Hopefully the modal has been shown,
      // now we don't want to show it again for this use of the app
      if (OneSignalV3.removeTriggerForKey) {
        OneSignalV3.removeTriggerForKey('ios_prompt');
      }
    }
  }, [currentMember?.isOnBoardingComplete, currentMember?.id]);

  return null;
};

OneSignalComp.propTypes = {
  currentMember: PropTypes.shape({
    isOnBoardingComplete: PropTypes.bool,
  }),
};

OneSignalComp.defaultProps = {
  currentMember: null,
};

const mapStateToProps = (state) => ({
  currentMember: state.backendService.currentMember,
});

export default connect(mapStateToProps)(OneSignalComp);
